import {
    HttpErrorResponse,
    provideHttpClient,
    withFetch,
    withInterceptors,
    withInterceptorsFromDi,
    withJsonpSupport,
} from '@angular/common/http';
import {
    enableProdMode,
    inject,
    provideAppInitializer,
    provideExperimentalZonelessChangeDetection,
} from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import {
    MAT_PAGINATOR_DEFAULT_OPTIONS,
    MatPaginatorDefaultOptions,
    MatPaginatorIntl,
} from '@angular/material/paginator';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { BASE_PATH } from '@buck-e-icp/interfaces';
import { provideEffects } from '@ngrx/effects';
import { provideStore, Store } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import { APP_CONFIG } from './app/app.config';
import routes from './app/app-routes';
import { AuthInterceptor } from './app/auth/interceptors/auth.interceptor';
import { provideIdpServiceConfig } from './app/auth/services';
import { AuthActions, AuthEffects, authFeature } from './app/auth/store';
import { BootstrapComponent } from './app/bootstrap/bootstrap.component';
import { LanguageService, provideLanguageService } from './app/core/i18n';
import {
    ErrorHandlingConfigurationBaseLoader,
    ErrorHandlingConfigurationLoader,
} from './app/error-handling/error-handling.config';
import { HttpErrorInterceptor } from './app/error-handling/interceptors';
import { ErrorService } from './app/error-handling/services';
import {
    getCurrentLanguage,
    HttpRequestFailed,
    NotificationEffects,
    storeDataFeature,
    translateFeature,
} from './app/shared/store';
import { StoreDataEffects } from './app/shared/store/store-data/store-data.effects';
import * as TranslateEffects from './app/shared/store/store-translate/translate.effect';
import { CustomMatPaginatorIntl } from './app/shared/utils';
import { TranslocoHttpLoader } from './app/transloco-http-loader';
import { environment } from './environments/environment';

export const errorHandlingFactory = (
    store: Store,
    errorService: ErrorService,
): ErrorHandlingConfigurationBaseLoader => {
    // Dispatches an action when we get a HttpErrorResponse (expect 401)
    const httpErrorResponseCallbackCallback = (error: HttpErrorResponse): void => {
        store.dispatch(
            HttpRequestFailed({
                response: errorService.serializeResponse(error),
            }),
        );
    };

    return new ErrorHandlingConfigurationLoader(httpErrorResponseCallbackCallback);
};

if (environment.production) {
    enableProdMode();
}
bootstrapApplication(BootstrapComponent, {
    providers: [
        provideStore({
            [translateFeature.name]: translateFeature.reducer,
            [authFeature.name]: authFeature.reducer,
            [storeDataFeature.name]: storeDataFeature.reducer,
        }),
        provideEffects(TranslateEffects, AuthEffects, StoreDataEffects),
        provideStoreDevtools({
            logOnly: environment.production,
            name: 'Buck-e Dashboard',
            maxAge: 25,
        }),
        provideEffects(NotificationEffects),
        {
            provide: ErrorHandlingConfigurationBaseLoader,
            useFactory: errorHandlingFactory,
            deps: [Store, ErrorService],
        },
        provideRouter(
            routes,
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
            withRouterConfig({ paramsInheritanceStrategy: 'always' }),
            withComponentInputBinding(),
        ),
        provideHttpClient(
            withFetch(),
            withInterceptorsFromDi(),
            withInterceptors([AuthInterceptor, HttpErrorInterceptor]),
            withJsonpSupport(),
        ),
        provideLanguageService({ selector: getCurrentLanguage, loader: TranslocoHttpLoader }),
        provideAppInitializer(() => {
            inject(Store).dispatch(AuthActions.startAutoLogin());
            // wait until translations are loaded
            inject(MatIconRegistry).setDefaultFontSetClass('material-symbols-outlined');
            return inject(LanguageService).initialize();
        }),
        provideIdpServiceConfig({
            idpUrl: environment.idpUrl,
            clientId: environment.idpClientId,
        }),
        provideAnimationsAsync(window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'noop' : 'animations'),
        provideExperimentalZonelessChangeDetection(),
        { provide: BASE_PATH, useValue: '' },
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: { appearance: 'outline' } satisfies MatFormFieldDefaultOptions,
        },
        {
            provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
            useValue: {
                showFirstLastButtons: true,
                pageSizeOptions: [10, APP_CONFIG.rowLimit, 100, 200, 500],
                pageSize: APP_CONFIG.rowLimit,
            } satisfies MatPaginatorDefaultOptions,
        },
        { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    ],
}).catch((err) => console.error(err));
